import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/useApi";
import { PageTitle, Stat, Panel, Loading, ErrorState } from "@/components/Common";
import { inr } from "@/lib/format";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const path = user.role === "admin" ? "/admin/dashboard" : "/merchant/dashboard";
  const { data, loading, error, reload } = useApi(path);

  if (loading && !data) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const f = data.fleet, t = data.transactions;

  return (
    <div>
      <PageTitle
        title={user.role === "admin" ? "Fleet Dashboard" : "My Dashboard"}
        subtitle={user.role === "admin" ? "Full fleet visibility across all machines" : "Overview of your assigned machines"}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat label="Total Machines" value={f.total} />
        <Stat label="Online" value={f.online} accent="#10B981" />
        <Stat label="Delayed" value={f.delayed} accent="#F59E0B" />
        <Stat label="Offline" value={f.offline} accent="#EF4444" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Revenue (Success)" value={inr(t.revenue_paise)} accent="#10B981" />
        <Stat label="Transactions" value={t.total} />
        <Stat label="Refunded" value={t.refunded} accent="#F59E0B" />
        <Stat label="Blocked Machines" value={f.blocked} accent="#EF4444" />
      </div>

      <Panel title={`Attention Required (${data.attention.length})`}>
        {data.attention.length === 0 ? (
          <div className="px-4 py-10 text-center text-muted-foreground text-sm">All clear — no active alerts.</div>
        ) : (
          <ul className="divide-y divide-border" data-testid="attention-list">
            {data.attention.map((a, i) => (
              <li
                key={`${a.machine_id}-${i}`}
                onClick={() => navigate(`/machines/${a.machine_id}`)}
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#161616] transition-colors"
                data-testid={`attention-item-${i}`}
              >
                <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: a.severity === "critical" ? "#EF4444" : "#F59E0B" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    <span className="mono">{a.machine_code}</span> · {a.machine_name}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{a.message}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-muted-foreground" />
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
